import type { MultilineContent } from "@repo/app-types/content/types";
import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import type {
  PlaybookCriterionNode,
  PlaybookId,
  PlaybookNodeId,
  PlaybookSectionNode,
  PlaybookTree,
} from "@repo/app-types/entities/playbook";
import { type FC, useCallback } from "react";
import type { PlaybookEditorData } from "../hooks/usePlaybookEditor.js";
import type {
  TestResultSelection,
  UseSampleToolResultsResult,
} from "../hooks/useSampleToolResults.js";
import { RecordingAssessmentEditor } from "./recordingAssessmentEditor.js";
import type { AssessmentTreeItemKey } from "./recordingAssessmentEditor/assessmentTreeHelpers.js";

export interface PlaybookEditorContainerProps {
  /** The current playbook data (controlled) */
  data: PlaybookEditorData | undefined;

  /** The initial outcome value (used to show change indicator) */
  initialOutcome: OutcomeDefinition | null | undefined;

  /** The live/published outcome for change comparison (draft dot + context diff). */
  liveOutcome?: OutcomeDefinition | null;

  /** The live/published summary template for change comparison. */
  liveSummaryTemplateBody?: MultilineContent | null;

  /** The live/published scope description for change comparison. */
  liveScopeDescription?: string | null;

  /**
   * Offer the Scope tile. Only the full playbook editor sets it: a voice
   * agent's playbook has no scope.
   */
  showsScope?: boolean;

  /** The live/published tree for change comparison (used to show edit indicators) */
  liveTree?: PlaybookTree;

  /** Whether the editor is read-only */
  isReadOnly?: boolean;

  /** Initial URL-backed assessment-tree selection. */
  initialSelectedKey?: AssessmentTreeItemKey;

  /** Callback when data changes */
  onChange: (changes: Partial<PlaybookEditorData>) => void;

  /** Callback when the user wants AI help optimizing a criterion */
  onOptimizeCriterion?: (criteria: PlaybookCriterionNode) => void;

  /** Callback when the user wants the AI to run the sample-distribution tool against a criterion as-is. */
  onTestCriterion?: (criteria: PlaybookCriterionNode) => void;

  /** Callback when the user wants AI help optimizing a section */
  onOptimizeSection?: (section: PlaybookSectionNode) => void;

  /** Callback when the user wants the AI to run the sample-applicability tool against a section as-is. */
  onTestSection?: (section: PlaybookSectionNode) => void;

  /** Callback when the user wants AI help optimizing the outcome rubric */
  onOptimizeOutcome?: (outcome: OutcomeDefinition) => void;

  /** Callback when the user wants the AI to run the sample-outcome tool against the outcome as-is. */
  onTestOutcome?: (outcome: OutcomeDefinition) => void;

  /** Absent where inline publishing isn't offered. */
  onPublishNode?: (nodeId: string) => void;

  onPublishOutcome?: () => void;

  onPublishSummaryTemplate?: () => void;

  onPublishScope?: () => void;

  publishingNodeId?: string | null;

  isPublishingOutcome?: boolean;

  isPublishingSummaryTemplate?: boolean;

  isPublishingScope?: boolean;

  /** Inline publish buttons wait while any publish or discard is in flight. */
  isPublishBlocked?: boolean;

  /** Whether Ask Alpharun is replying or loading a thread */
  isCopilotBusy?: boolean;

  /**
   * Sample-tool results collected during this editing session. The map is
   * keyed by `proposal_id`; consumers look up an entry by the
   * currently-selected criterion's `playbook_node_id`.
   */
  sampleToolResults?: UseSampleToolResultsResult["results"];

  /** Drop the saved sample-tool result for the given criterion id. */
  onResolveSampleToolResultForNode?: (
    playbookNodeId: PlaybookNodeId,
    status: "applied" | "discarded",
  ) => void;
  playbookId?: PlaybookId | null;
  /** Restrict outcome editing to transcript-derived outcomes (voice agents). */
  isTranscriptOutcomeOnly?: boolean;

  /**
   * Selection signal from the assistant sidebar. When the identity (object
   * reference) changes, the assessment editor selects the referenced
   * criterion. The `nonce` lets the same id re-fire selection. `selection`
   * says which tree key to select: an existing node, the outcome pane, or a
   * ghost row for a create proposal.
   */
  criterionSelectionRequest?: {
    id: PlaybookNodeId;
    selection: TestResultSelection;
    nonce: number;
  } | null;
  /** Reports the editor's current selection up to flag the "Viewing" row. */
  onCurrentSelectionChange?: (key: AssessmentTreeItemKey | undefined) => void;
}

export const PlaybookEditorContainer: FC<PlaybookEditorContainerProps> = ({
  data,
  initialOutcome,
  liveOutcome,
  liveSummaryTemplateBody,
  liveScopeDescription,
  showsScope = false,
  liveTree,
  isReadOnly = false,
  initialSelectedKey,
  onChange,
  onOptimizeCriterion,
  onTestCriterion,
  onOptimizeSection,
  onTestSection,
  onOptimizeOutcome,
  onTestOutcome,
  onPublishNode,
  onPublishOutcome,
  onPublishSummaryTemplate,
  onPublishScope,
  publishingNodeId,
  isPublishingOutcome,
  isPublishingSummaryTemplate,
  isPublishingScope,
  isPublishBlocked,
  isCopilotBusy = false,
  sampleToolResults,
  onResolveSampleToolResultForNode,
  playbookId,
  isTranscriptOutcomeOnly = false,
  criterionSelectionRequest,
  onCurrentSelectionChange,
}) => {
  // Handlers that delegate to onChange
  const handleTreeChange = useCallback(
    (template: PlaybookTree) => {
      onChange({ tree: template });
    },
    [onChange],
  );

  const handleOutcomeChange = useCallback(
    (outcome: OutcomeDefinition | null) => {
      onChange({ outcome });
    },
    [onChange],
  );

  const handleSummaryTemplateBodyChange = useCallback(
    (summaryTemplateBody: MultilineContent | null) => {
      onChange({ summaryTemplateBody });
    },
    [onChange],
  );

  const handleScopeDescriptionChange = useCallback(
    (scopeDescription: string | null) => {
      onChange({ scopeDescription });
    },
    [onChange],
  );

  // Show loading or empty state
  if (!data || initialOutcome === undefined) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border border-border-light bg-gray-50 text-muted-foreground">
        <p className="text-sm">Loading playbook...</p>
      </div>
    );
  }

  return (
    <RecordingAssessmentEditor
      tree={data.tree}
      liveTree={liveTree}
      onChange={handleTreeChange}
      isReadOnly={isReadOnly}
      outcomeHandler={{
        outcome: data.outcome,
        initialOutcome,
        liveOutcome,
        onChange: handleOutcomeChange,
        onPublish: onPublishOutcome,
        isPublishing: isPublishingOutcome,
      }}
      summaryTemplateHandler={{
        body: data.summaryTemplateBody,
        liveBody: liveSummaryTemplateBody,
        onChange: handleSummaryTemplateBodyChange,
        onPublish: onPublishSummaryTemplate,
        isPublishing: isPublishingSummaryTemplate,
      }}
      scopeHandler={
        showsScope
          ? {
              description: data.scopeDescription,
              liveDescription: liveScopeDescription,
              onChange: handleScopeDescriptionChange,
              onPublish: onPublishScope,
              isPublishing: isPublishingScope,
            }
          : undefined
      }
      initialSelectedKey={initialSelectedKey}
      onOptimizeCriterion={onOptimizeCriterion}
      onTestCriterion={onTestCriterion}
      onOptimizeSection={onOptimizeSection}
      onTestSection={onTestSection}
      onOptimizeOutcome={onOptimizeOutcome}
      onTestOutcome={onTestOutcome}
      onPublishNode={onPublishNode}
      publishingNodeId={publishingNodeId}
      isPublishBlocked={isPublishBlocked}
      isCopilotBusy={isCopilotBusy}
      sampleToolResults={sampleToolResults}
      onResolveSampleToolResultForNode={onResolveSampleToolResultForNode}
      playbookId={playbookId}
      isTranscriptOutcomeOnly={isTranscriptOutcomeOnly}
      criterionSelectionRequest={criterionSelectionRequest}
      onCurrentSelectionChange={onCurrentSelectionChange}
    />
  );
};
